/**
 * Wall crusher SPIKES — the menacing teeth on each crusher's INNER face (the edge
 * that faces the survivable lane). *Core decides, Phaser renders*: core owns the wall
 * geometry + its creep→crush→open motion; this paints glowing teeth that ride the
 * wall's moving inner edge and point at the lane. The two walls of a pair are offset
 * by half a tooth so their teeth INTERLOCK (read as one meshing crusher) when they
 * close in.
 *
 * One pooled {@link Phaser.GameObjects.Graphics} per wall id, redrawn each frame
 * (cheap — a few walls, ~15 teeth each). Decorative only: collision is the rect in
 * core, so the shallow teeth never make the lane unfair.
 */
import Phaser from 'phaser';
import type { RectHazard, Viewport } from '../core';
import { HAZARD_COLORS } from './textures';

/** Above the wall body (hazards draw at 5), below the player (10). */
const SPIKE_DEPTH = 6;
/** Tooth spacing + how far the teeth jut toward the lane at full crush (px, canonical). */
const PITCH = 46;
const DEPTH = 24;
/** Telegraph teeth are shallower (they GROW + ignite when the wall goes lethal). */
const TELEGRAPH_DEPTH_SCALE = 0.4;

export class WallSpikeFx {
  private readonly scene: Phaser.Scene;
  private readonly free: Phaser.GameObjects.Graphics[] = [];
  private readonly all: Phaser.GameObjects.Graphics[] = [];
  private readonly state = new Map<number, Phaser.GameObjects.Graphics>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Redraw the spikes on one wall's inner face. `phase` is the telegraph progress
   * (0..1 while creeping, ≥1 once active) — while creeping the teeth are SHALLOW and a
   * faint TEAL (harmless preview, matching the body), then IGNITE to full depth + hot
   * red/white the instant the wall goes lethal, so they never mis-signal danger.
   */
  update(h: RectHazard, view: Viewport, elapsedMs: number, phase: number): void {
    let g = this.state.get(h.id);
    if (!g) {
      g = this.acquire();
      this.state.set(h.id, g);
    }
    g.clear();
    const pulse = 0.82 + 0.18 * Math.sin(elapsedMs / 90);
    const active = phase >= 1;
    const depth = DEPTH * (active ? 1 : TELEGRAPH_DEPTH_SCALE);
    // Harmless creep: dim teal, ramping like the body's telegraph (0.18 → 0.55).
    const alpha = active ? 1 : 0.18 + 0.37 * phase;

    if (h.crushAxis === 'y') {
      // Top/bottom wall: horizontal inner edge, teeth point along y toward the lane.
      const isLow = h.y + h.h / 2 < view.height / 2;
      const edgeY = isLow ? h.y + h.h : h.y;
      const dir = isLow ? 1 : -1;
      const offset = isLow ? 0 : PITCH / 2;
      const apexY = edgeY + dir * depth;
      for (let x = h.x + offset; x < h.x + h.w; x += PITCH) {
        const a = x;
        const b = Math.min(x + PITCH, h.x + h.w);
        this.tooth(g, a, edgeY, b, edgeY, (a + b) / 2, apexY, pulse, active, alpha);
      }
    } else {
      // Left/right wall: vertical inner edge, teeth point along x toward the lane.
      const isLow = h.x + h.w / 2 < view.width / 2;
      const edgeX = isLow ? h.x + h.w : h.x;
      const dir = isLow ? 1 : -1;
      const offset = isLow ? 0 : PITCH / 2;
      const apexX = edgeX + dir * depth;
      for (let y = h.y + offset; y < h.y + h.h; y += PITCH) {
        const a = y;
        const b = Math.min(y + PITCH, h.y + h.h);
        this.tooth(g, edgeX, a, edgeX, b, apexX, (a + b) / 2, pulse, active, alpha);
      }
    }
  }

  /** Release the spikes for any wall id no longer live this step. */
  sweep(alive: Set<number>): void {
    for (const [id, g] of this.state) {
      if (alive.has(id)) continue;
      this.release(g);
      this.state.delete(id);
    }
  }

  reset(): void {
    for (const g of this.state.values()) this.release(g);
    this.state.clear();
  }

  destroy(): void {
    this.state.clear();
    for (const g of this.all) g.destroy();
    this.all.length = 0;
    this.free.length = 0;
  }

  /* --------------------------------------------------------------------- */

  /**
   * Draw one tooth: a triangle with base `(bx0,by0)–(bx1,by1)` on the wall edge and
   * apex `(ax,ay)` jutting toward the lane. When `active`, a glowing-steel spike
   * (dark-base → hot orange → white apex); while telegraphing, a single faint TEAL
   * triangle at `alpha` (matching the body's harmless creep look).
   */
  private tooth(
    g: Phaser.GameObjects.Graphics,
    bx0: number,
    by0: number,
    bx1: number,
    by1: number,
    ax: number,
    ay: number,
    pulse: number,
    active: boolean,
    alpha: number,
  ): void {
    if (!active) {
      g.fillStyle(HAZARD_COLORS.turretBody, alpha);
      g.fillTriangle(bx0, by0, bx1, by1, ax, ay);
      return;
    }
    g.fillStyle(0x8f1f14, 0.95);
    g.fillTriangle(bx0, by0, bx1, by1, ax, ay);
    // Bright outer half (midpoints of the slanted edges → apex).
    const m0x = (bx0 + ax) / 2;
    const m0y = (by0 + ay) / 2;
    const m1x = (bx1 + ax) / 2;
    const m1y = (by1 + ay) / 2;
    g.fillStyle(0xff6a2e, pulse);
    g.fillTriangle(m0x, m0y, m1x, m1y, ax, ay);
    // White-hot tip.
    g.fillStyle(0xffe9c0, pulse);
    g.fillTriangle((m0x + ax) / 2, (m0y + ay) / 2, (m1x + ax) / 2, (m1y + ay) / 2, ax, ay);
  }

  private acquire(): Phaser.GameObjects.Graphics {
    const g = this.free.pop() ?? this.make();
    g.setActive(true).setVisible(true);
    return g;
  }
  private release(g: Phaser.GameObjects.Graphics): void {
    g.clear();
    g.setActive(false).setVisible(false);
    this.free.push(g);
  }
  private make(): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics().setDepth(SPIKE_DEPTH);
    this.all.push(g);
    return g;
  }
}
